import { type SxProps, type Theme } from "@mui/material";

import { mergeSx } from "ui/merge-sx";

import {
  ACTIONS_PADDING_TOP,
  BODY_HALO,
  CLOSE_INSET,
  CONTAINER_PADDING,
  CONTAINER_SPACING,
  DETAILS_SPACING,
  GLYPH_TO_CONTENT,
  HEADING_ICON_SPACING,
  HEADING_SPACING,
  HEADING_PADDING_RIGHT,
  ICON_SIZE,
  MEDIUM_MAX_HEIGHT,
  PADDING_LEFT_BASIC,
  PADDING_LEFT_WITH_RAIL,
  RAIL_COLOR,
  RAIL_WIDTH,
} from "ui/dialog/dialog.constants";
import { type DialogIntent, type DialogSize } from "ui/dialog/dialog.types";

import { shadows } from "design-tokens";

export const PAPER_SX: SxProps<Theme> = {
  position: "relative",
  display: "flex",
  flexDirection: "row",
  boxShadow: shadows.md,
  pt: `${CONTAINER_PADDING}px`,
  pr: `${CONTAINER_PADDING}px`,
  pb: `${CONTAINER_PADDING}px`,
  borderLeftWidth: `${RAIL_WIDTH}px`,
  borderLeftStyle: "solid",
  gap: `${GLYPH_TO_CONTENT}px`,
};

export const PAPER_SX_BY_INTENT = {
  inherit: { pl: `${PADDING_LEFT_BASIC}px`, borderLeftColor: "transparent" },
  success: (theme) => ({
    pl: `${PADDING_LEFT_WITH_RAIL}px`,
    borderLeftColor: RAIL_COLOR.success(theme),
  }),
  warning: (theme) => ({
    pl: `${PADDING_LEFT_WITH_RAIL}px`,
    borderLeftColor: RAIL_COLOR.warning(theme),
  }),
  error: (theme) => ({
    pl: `${PADDING_LEFT_WITH_RAIL}px`,
    borderLeftColor: RAIL_COLOR.error(theme),
  }),
} satisfies Record<DialogIntent, SxProps<Theme>>;

export const PAPER_SX_BY_SIZE = {
  small: { width: 640 },
  medium: { width: 640, maxHeight: MEDIUM_MAX_HEIGHT },
  large: { width: 640, height: "80vh" },
  xlarge: { width: "90vw", height: "80vh" },
} satisfies Record<DialogSize, SxProps<Theme>>;

export const getPaperWidthSx = (width: number | undefined): SxProps<Theme> | undefined =>
  width === undefined ? undefined : { width };

export const CLOSE_BUTTON_SX: SxProps<Theme> = {
  position: "absolute",
  right: `${CLOSE_INSET}px`,
  top: `${CLOSE_INSET}px`,
  zIndex: 10,
};

const GLYPH_SX: SxProps<Theme> = {
  width: ICON_SIZE,
  height: ICON_SIZE,
  flexShrink: 0,
  alignSelf: "flex-start",
};

export const GLYPH_SX_BY_INTENT = {
  success: mergeSx(GLYPH_SX, (theme) => ({ color: RAIL_COLOR.success(theme) })),
  warning: mergeSx(GLYPH_SX, (theme) => ({ color: RAIL_COLOR.warning(theme) })),
  error: mergeSx(GLYPH_SX, (theme) => ({ color: RAIL_COLOR.error(theme) })),
} satisfies Record<Exclude<DialogIntent, "inherit">, SxProps<Theme>>;

export const CONTENT_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  gap: `${CONTAINER_SPACING}px`,
};

export const HEADER_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: `${HEADING_SPACING}px`,
  flexShrink: 0,
};

/** Figma's step line: `subtitle1` in `grey[400]`. */
export const STEP_INDICATOR_SX: SxProps<Theme> = (theme) => ({ color: theme.palette.grey[400] });

export const HEADLINE_SX: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "center",
  gap: `${HEADING_ICON_SPACING}px`,
  flexShrink: 0,
  pr: `${HEADING_PADDING_RIGHT}px`,
  color: theme.palette.secondary.main,
});

export const HEADLINE_ICON_SX: SxProps<Theme> = (theme) => ({
  display: "inline-flex",
  flexShrink: 0,
  color: theme.palette.secondary.light,
  "& svg": { width: ICON_SIZE, height: ICON_SIZE },
});

export const DESCRIPTION_SX: SxProps<Theme> = (theme) => ({
  flexShrink: 0,
  color: theme.palette.text.primary,
});

export const BODY_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  gap: `${DETAILS_SPACING}px`,
  p: `${BODY_HALO}px`,
  m: `-${BODY_HALO}px`,
};

export const BODY_SCROLL_SX: SxProps<Theme> = { overflowY: "auto", overflowX: "hidden" };

export const ACTIONS_SX: SxProps<Theme> = { flexShrink: 0, pt: `${ACTIONS_PADDING_TOP}px` };
